import * as Service from 'types/service';
import { PartyState } from 'types/service';
import { dashboard } from 'types';
import { getHasAnsweredQuestions } from '@tock/shared/utils/questionUtils';
import {
  hasBusinessProfiles,
  isPartyOnlineWalkIn,
  isPartyQuotedWalkIn,
  isPartyWalkIn,
  textMessageHasStatusIcon,
} from 'utils/partyUtils';
import { visitGeneralNotes } from 'utils/purchaseUtils';

/**
 * This is used as a data source for different PartyRedesign* components. It, and the
 * related utilities below, allows us to do renders
 * based on Service.Party or on dashboard.CalendarWaitlist data with equal flexibility.
 */
export type PartyForCard = Service.Party | dashboard.CalendarWaitlist;

export function isServiceParty(party: PartyForCard): party is Service.Party {
  return 'patronInfo' in party;
}

export function getPatron(party: PartyForCard) {
  return isServiceParty(party)
    ? party?.purchase?.dinerPatron?.[0]
    : (party?.guestProfile?.patron ?? undefined);
}

export function getHasGlobalDiningAccess(party: PartyForCard) {
  return party ? !!getPatron(party)?.hasGlobalDiningAccess : false;
}

export function getPatronKeyContactAttributes(party: PartyForCard): {
  firstName?: string;
  lastName?: string;
  email?: string;
  lastNameFirstName?: string;
} {
  if (isServiceParty(party)) {
    const {
      patronInfo: { firstName, lastName, email },
      lastNameFirstName,
    } = party;

    if (party.purchase?.dinerPatron?.[0]) {
      return {
        firstName: party.purchase.dinerPatron[0].firstName || '',
        lastName: party.purchase.dinerPatron[0].lastName || '',
        email: party.purchase.dinerPatron[0].email || '',
        lastNameFirstName: `${party.purchase.dinerPatron[0].lastName} ${party.purchase.dinerPatron[0].firstName}`,
      };
    }
    return {
      firstName,
      lastName,
      email,
      lastNameFirstName,
    };
  } else {
    const patron = party.guestProfile?.patron;
    const firstName = patron?.firstName ?? undefined;
    const lastName = patron?.lastName ?? undefined;
    return {
      firstName,
      lastName,
      email: patron?.email ?? undefined,
      lastNameFirstName:
        [lastName, firstName].filter((s) => s !== undefined).join(',') || undefined,
    };
  }
}

export function getGuestProfile(party: PartyForCard) {
  if (isServiceParty(party)) {
    return (hasBusinessProfiles(party) && party.primaryDinerProfile) || undefined;
  } else {
    return (party.guestProfile as dashboard.GuestProfile) ?? undefined;
  }
}

export function getExpectedPartySize(party: PartyForCard) {
  return isServiceParty(party) ? party.expectedPartySize : party.desiredSize;
}

export function getIsLargeParty(party: PartyForCard, minLargePartySize?: number) {
  if (isServiceParty(party)) {
    return party.isLargeParty;
  } else {
    return minLargePartySize ? party.desiredSize >= minLargePartySize : false;
  }
}

export function getExpectedReservationTime(party: PartyForCard) {
  return isServiceParty(party) ? party.expectedReservationTime : undefined;
}

export function getTicketType(party: PartyForCard) {
  return (
    (isServiceParty(party) ? party.purchase?.ticketType : party.desiredExperience) ?? undefined
  );
}

export function getVisitNote(party: PartyForCard) {
  return isServiceParty(party)
    ? ((party.purchase && visitGeneralNotes(party.purchase)?.note) ?? '')
    : party.guestNote;
}

export function getVisitTags(party: PartyForCard) {
  return (isServiceParty(party) && party.visit?.tag) || [];
}

export function getQuickTags(party: PartyForCard) {
  return isServiceParty(party) ? party.tag : [];
}

export function getDescriptionString(party: PartyForCard) {
  const experienceShortCode = getTicketType(party)?.shortCode ?? '';
  if (isServiceParty(party) && isPartyWalkIn(party)) {
    return getWalkInDescriptionString(party);
  } else {
    return experienceShortCode;
  }
}

const expectedStates: PartyState[] = [
  'EXPECTED',
  'PARTIALLY_ARRIVED',
  'ARRIVED',
  'PARTIALLY_SEATED',
];
function getWalkInDescriptionString(walkInParty: Service.Party): string {
  const isExpectedWalkIn =
    !isPartyQuotedWalkIn(walkInParty) && expectedStates.includes(walkInParty.state);
  let description = isExpectedWalkIn ? 'Expected Walk-in' : 'Walk-in';
  if (isPartyOnlineWalkIn(walkInParty)) {
    description += ' (online)';
  }

  return description;
}

export function getSeatingAreaName(party: PartyForCard) {
  return (isServiceParty(party) && party.purchase?.seatingAreaName) || '';
}

export function getSmsInfo(party: PartyForCard) {
  if (isServiceParty(party)) {
    return {
      confirmedViaSms: party.purchase?.confirmedViaSms ?? false,
      hasUnreadSms: party.hasUnreadTextMessage,
      lastSmsStatus: party.textMessage.filter(textMessageHasStatusIcon)?.at(-1)?.status,
    };
  } else {
    return {
      confirmedViaSms: false,
      hasUnreadSms: false,
      lastSmsStatus: undefined,
    };
  }
}

export function getPurchaseInfo(party: PartyForCard) {
  if (isServiceParty(party)) {
    return {
      hasAnsweredQuestion: getHasAnsweredQuestions(party.purchase?.question ?? []),
      hasPurchasedSupplements: (party.purchase?.purchasedOption ?? []).length > 0,
      hasDelivered: !!party.purchase?.visit?.delivered,
      hasDeliverySignatureRequirement: !!party.purchase?.hasDeliverySignatureRequirement,
      requiresPayment: party.requiresPayment,
    };
  } else {
    return {
      hasAnsweredQuestion: false,
      hasPurchasedSupplements: false,
      hasDelivered: false,
      hasDeliverySignatureRequirements: false,
      requiresPayment: false,
    };
  }
}
